import { MessageBarType } from "office-ui-fabric-react";
import * as React from "react";
import { Component, Fragment } from "react";
import { connect } from "react-redux";
import School from "../../models/School";
import { sortYearGroupingsByIsAfterValues, YearGrouping } from "../../models/SchoolWorkloadConfig";
import SubjectDataReport from "../../models/SubjectDataReport";
import { SubjectMerge } from "../../models/SubjectMerge";
import Actions from "../../redux/Actions";
import { IRootState } from "../../store";
import DispatchProps from "../../utils/DispatchProps";
import ApiService from "../ApiService";
import Table, { TableColumn } from "../Table";
import TableCell from "../TableCell";
import HelpBox from "./admin/HelpBox";
import LoadingPage from "../LoadingPage";
import AcademicYearPicker from "../AcademicYearPicker";
import H from "history";
import SchoolPageTitle from "../SchoolPageTitle";
import SchoolMenu from "../SchoolMenu";

interface SchoolSubjectDataPageProps {
    tenantId: string;
    schoolCode: string;
    apiService: ApiService;
    history: H.History;
    academicYear: number;
}

interface SchoolSubjectDataPageState {
    isLoading: boolean;
    school?: School;
    data?: SubjectDataReport
}

class SchoolSubjectDataPage extends Component<SchoolSubjectDataPageProps & DispatchProps, SchoolSubjectDataPageState> {

    constructor(props: SchoolSubjectDataPageProps & DispatchProps) {
        super(props);

        this.state = {
            isLoading: false,
        };
    }

    async componentDidMount() {
        if (this.props.tenantId && this.props.schoolCode) {
            await this.loadDataAndSetState()
        }
    }

    async componentDidUpdate(prevProps: Readonly<SchoolSubjectDataPageProps & DispatchProps>, prevState: any): Promise<void> {
        if (this.props.tenantId !== prevProps.tenantId || this.props.schoolCode !== prevProps.schoolCode || this.props.academicYear !== prevProps.academicYear) {
            await this.loadDataAndSetState()
        }
    }

    private async loadDataAndSetState() {
        this.setState({ isLoading: true });

        try {
            const data = await this.props.apiService.getSubjectsForDataPage(this.props.tenantId, this.props.schoolCode, this.props.academicYear);

            this.setState({data})
        }
        catch (e) {
            console.log(e)
            Actions.showMessageBar(`Unable to fetch data: ${e}`, MessageBarType.error);
        }

        const school = await this.props.apiService.getSchool(this.props.tenantId, this.props.schoolCode)

        this.setState({
            school,
            isLoading: false
        })
    }

    private ExcludesColumns: TableColumn[] = [
        {
            title: "Subject Name",
            propertyPath: "subjectName",
            flex: 1
        },
        {
            title: "Subject Code",
            propertyPath: "subjectCode",
            flex: 1
        }
    ];

    private subjectColumns: TableColumn[] = [
        {
            title: "Subject Name",
            propertyPath: "subjectName",
            flex: 1
        },
        {
            title: "Subject Code",
            propertyPath: "subjectCode",
            flex: 1
        },
        {
            title: "Class Count",
            propertyPath: "classCount",
            flex: 1
        }
    ];

    private MergesColumns: TableColumn[] = [
        {
            title: "Merged Subject Name",
            propertyPath: "targetSubjectName",
            flex: 1
        },
        {
            title: "Merged Subject Code",
            propertyPath: "targetSubjectCode",
            flex: 1
        },
        {
            title: "Source Subject Codes",
            propertyPath: "sourceSubjectCodes",
            flex: 1
        }
    ];

    private YearGroupColumns: TableColumn[] = [
        {
            title: "Name",
            propertyPath: "name",
            flex: 1
        },
        {
            title: "Years",
            propertyPath: "years",
            flex: 3
        },
    ];

    private cellForColumn = (item: YearGrouping, column: TableColumn, columnIndex: number): React.ReactElement<TableCell> | undefined => {

        if (column.propertyPath === "years") {
            return (
                <TableCell flex={column.flex} key={columnIndex}>
                    <span>{item.years?.join(',')}</span>
                </TableCell>
            );
        }
    };

    private cellForMerges = (item: SubjectMerge, column: TableColumn, columnIndex: number): React.ReactElement<TableCell> | undefined => {

        if (column.propertyPath === "sourceSubjectCodes") {
            return (
                <TableCell flex={column.flex} key={columnIndex}>
                    <span>{item.sourceSubjectCodes?.join(',')}</span>
                </TableCell>
            );
        }
    };

    private renderContent() {
        if (this.state.isLoading) {
            return <LoadingPage />;
        }

        const excludesDescription = `These subjects will not be processed in either our class teams or subject teams provisioning process`;

        return <Fragment>
            <HelpBox Icon={"SearchData"} TitleText={`View School Data`} HelpBoxText={``} />

            <div className={"cdb-admin-content cdb-admin-content--column"}>
                <div className="flex-row flex--justify-content-space-evenly">
                    <Table
                        title="Excludes"
                        columns={this.ExcludesColumns}
                        items={this.state.data ? this.state.data.excludedSubjects : []}
                        className="flex-item--flex-1 cdb-table--enable-hover"
                        loading={this.state.isLoading}
                        enableToolTip={true}
                        toolTipDescription={excludesDescription}>
                    </Table>
    
                    <Table
                        title="Merges and Renames"
                        columns={this.MergesColumns}
                        items={this.state.data ? this.state.data.subjectMerges : []}
                        className="flex-item--flex-1 cdb-table--enable-hover"
                        loading={this.state.isLoading}
                        cellForColumn={this.cellForMerges}>
                    </Table>
                </div>
    
    
                <div style={{ display: "flex", alignItems: "baseline" }}>
                    <AcademicYearPicker history={this.props.history} />
                </div>
                <Table
                    title="Subjects"
                    columns={this.subjectColumns}
                    items={this.state.data ? this.state.data.subjects : []}
                    className="cdb-table--enable-hover"
                    loading={this.state.isLoading}>
                </Table>
    
                <Table
                    title="Year Groupings"
                    columns={this.YearGroupColumns}
                    items={this.state.data ? sortYearGroupingsByIsAfterValues(this.state.data.yearGroupings) : []}
                    loading={this.state.isLoading}
                    className={"cdb-table--enable-hover"}
                    cellForColumn={this.cellForColumn}>
                </Table>
            </div>
        </Fragment>
    }

    public render(): JSX.Element {
        return (
            <Fragment>
                <SchoolPageTitle tenantId={this.props.tenantId} schoolCode={this.props.schoolCode} title="Synced Data" reverse={true} />
                <SchoolMenu prefix="School Data" />
                {this.renderContent()}
            </Fragment>
        );
    }
}

export default connect(
    (state: IRootState, ownProps: any): SchoolSubjectDataPageProps => {
        const academicYear = AcademicYearPicker.fromQueryStringOrDefault(ownProps.history.location.search);

        return {
            tenantId: ownProps.match.params.tenantId,
            schoolCode: ownProps.match.params.schoolCode,
            apiService: state.auth.apiService,
            academicYear: academicYear,
            history: ownProps.history,
        };
    }
)(SchoolSubjectDataPage)